import { FlagOutlined, PlusCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Form, Input, InputNumber, message, Modal, Row, Select, Spin } from 'antd';
import React, { useState } from 'react';
import Helmet from 'react-helmet';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import MainContent from '@/layouts/MainContent';
import { useTranslation } from 'react-i18next';
import { formatApiDate, formatDate, formatDisplayDate, ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import {
  BlobStorageContainerType,
  DiscountApplicationTarget,
  DiscountCompanyType,
  DiscountUsageType,
  DiscountUserType,
  DiscountValueType,
} from '@/constants';
import AppDatePicker from '@/components/FormItems/DatePicker';
import moment from 'moment';
import PassengerInput from '@/components/FormItems/PassengerInput';
import { CreateUpdateGlobalDiscountModel, DiscountModel } from '@/__generated';
import { apiSdk } from '@/httpClient';
import BooleanTag from '@/components/app/BooleanTag';
import SingleImageUpload from '@/components/FormItems/SingleImageUpload';
import CompanyInput from '@/components/FormItems/CompanyInput';
import CityInput from '@/components/FormItems/CityInput';
import CountryInput from '@/components/FormItems/CountryInput';
import RegionInput from '@/components/FormItems/RegionInput';

const PAGE_TITLE = 'passengerDiscountPage_Title';

const columns: IColumnType<DiscountModel>[] = [
  {
    title: 'passengerDiscountPage_PromoCode',
    dataIndex: 'promoCode',
    ellipsis: true,
    defaultSortOrder: 'ascend',
    render: v => <b>{v}</b>,
    width: 250,
  },
  {
    title: 'passengerDiscountPage_AutoApply',
    dataIndex: 'autoApply',
    render: v => <BooleanTag value={v} />,
    width: 140,
  },
  {
    title: 'passengerDiscountPage_RefNo',
    dataIndex: 'refNo',
    width: 250,
  },
  {
    title: 'discountsPage_StartDate',
    dataIndex: 'fromDate',
    sorter: true,
    ellipsis: true,
    render: v => formatDisplayDate(v),
    width: 120,
  },
  {
    title: 'discountsPage_EndDate',
    dataIndex: 'toDate',
    sorter: true,
    ellipsis: true,
    render: v => formatDisplayDate(v),
    width: 120,
  },
  {
    title: 'passengerDiscountPage_ShortDescription',
    dataIndex: 'shortDescription',
    ellipsis: true,
  },
  {
    title: 'passengerDiscountPage_AutoApply',
    dataIndex: 'autoApply',
    render: v => <BooleanTag value={v} />,
    width: 140,
  },
  {
    title: 'base.Active',
    dataIndex: 'isActive',
    render: v => <BooleanTag value={v} />,
    width: 140,
  },
];

const filters: Array<IFilterItem> = [
  { type: 'TEXT', name: 'promoCode', label: 'passengerDiscountPage_PromoCode' },
  { type: 'DATE', name: 'fromDate', label: 'discountsPage_StartDate', parseValue: v => formatDate(v) },
  { type: 'DATE', name: 'toDate', label: 'discountsPage_EndDate', parseValue: v => formatDate(v) },
  { type: 'TEXT', name: 'shortDescription', label: 'passengerDiscountPage_ShortDescription' },
];

const PassengerDiscountPage = () => {
  const [t] = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [discount, setDiscount] = React.useState<CreateUpdateGlobalDiscountModel>();
  const createEntity = values => {
    setLoading(true);
    apiSdk.GlobalDiscountService.create({ body: toSubmitValue(values) })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const toSubmitValue = v => {
    return {
      ...v,
      fromDate: v.fromDate ? formatApiDate(v.fromDate) : undefined,
      toDate: v.toDate ? formatApiDate(v.toDate) : undefined,
      discountNewUserAfterDate: v.discountNewUserAfterDate ? formatApiDate(v.discountNewUserAfterDate) : undefined,
      discountOldUserBeforeDate: v.discountOldUserBeforeDate ? formatApiDate(v.discountOldUserBeforeDate) : undefined,
    };
  };
  const updateEntity = values => {
    setLoading(true);
    apiSdk.GlobalDiscountService.update({ body: toSubmitValue(values) })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const editDiscount = (id: string) => {
    setFormVisible(true);
    setLoading(true);
    apiSdk.GlobalDiscountService.getDetails({ id: id })
      .then(res => {
        setDiscount(res);
        form.setFieldsValue({
          ...res,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const discountCompany: number = Form.useWatch('discountCompany', form);
  const discountUsage: number = Form.useWatch('discountUsage', form);
  const countryId = Form.useWatch('countryId', form);
  const regionId = Form.useWatch('regionId', form);
  const discountUser = Form.useWatch('discountUser', form);

  return (
    <MainContent
      title={t(PAGE_TITLE)}
      extraBtns={[
        <Button
          key="create"
          type="primary"
          className="nta-create-btn"
          icon={<PlusCircleOutlined />}
          onClick={() => {
            form.resetFields();
            setDiscount(undefined);
            setFormVisible(true);
          }}
        >
          {t('createNew')}
        </Button>,
      ]}
      icon={<FlagOutlined style={{ fontSize: 16 }} />}
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable<DiscountModel>
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          url="/global-discounts/get-discounts"
          bordered
          order_by="createdOn"
          order_by_asc={false}
          allowEdit={true}
          onEdit={record => {
            editDiscount(record.id || '');
          }}
          allowDelete={true}
          delete_url="/global-discounts/delete"
          filters={filters}
        ></ZTable>
        {formVisible ? (
          <Modal
            open={formVisible}
            maskClosable={false}
            style={{ top: 10 }}
            width={ModalWidth.large}
            className="nta-custom-form"
            title={!discount ? t('discountsPage_CreateDiscount') : t('discountsPage_UpdateDiscount')}
            forceRender={true}
            okText={t('save')}
            cancelText={t('cancel')}
            okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
            onCancel={() => {
              form.resetFields();
              setFormVisible(false);
            }}
            onOk={() => {
              form
                .validateFields()
                .then(values => {
                  if (values.id) {
                    updateEntity(values);
                  } else {
                    createEntity(values);
                  }
                })
                .catch(info => {});
            }}
          >
            <Spin spinning={loading}>
              <Form
                form={form}
                size="middle"
                layout="horizontal"
                labelAlign="right"
                labelCol={{ md: 10 }}
                initialValues={{
                  autoApply: true,
                  isActive: true,
                  applicationTarget: [DiscountApplicationTarget.PassengerMobile, DiscountApplicationTarget.PassengerWeb],
                  discountCompany: DiscountCompanyType.AllCompanies,
                  discountType: DiscountValueType.PercentageBase,
                  discountUser: DiscountUserType.AllUsers,
                  discountUsage: DiscountUsageType.MultipleTimes,
                }}
              >
                <Form.Item name="id" className="hidden">
                  <Input type="hidden" />
                </Form.Item>

                <Row gutter={ROW_GUTTER}>
                  <Col md={12}>
                    <Form.Item label={t('passengerDiscountPage_PromoCode')} name="promoCode" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Input />
                    </Form.Item>
                    <Form.Item label={t('passengerDiscountPage_RefNo')} name="refNo" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Input />
                    </Form.Item>

                    <Form.Item label={t('passengerDiscountPage_AutoApply')} name="autoApply" valuePropName="checked">
                      <Checkbox></Checkbox>
                    </Form.Item>
                    <Form.Item label={t('base.Active')} name="isActive" valuePropName="checked">
                      <Checkbox></Checkbox>
                    </Form.Item>
                    <Form.Item noStyle dependencies={['toDate']}>
                      {({ getFieldValue }) => {
                        const endDate = getFieldValue('toDate');
                        return (
                          <Form.Item label={t('discountsPage_StartDate')} name="fromDate" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                            <AppDatePicker
                              className="w-100"
                              disabledDate={d => {
                                if (endDate) {
                                  return d.endOf('day') > moment(endDate).endOf('day');
                                }
                                return false;
                              }}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>

                    <Form.Item noStyle dependencies={['fromDate']}>
                      {({ getFieldValue }) => {
                        const startDate = getFieldValue('fromDate');
                        return (
                          <Form.Item label={t('discountsPage_EndDate')} name="toDate" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                            <AppDatePicker
                              className="w-100"
                              disabledDate={d => {
                                if (startDate) {
                                  return d.endOf('day') < moment(startDate).endOf('day');
                                }
                                return false;
                              }}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>

                    <Form.Item label={t('discountsPage_ValueType')} name="discountType" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Select options={DiscountValueType.__getSelectOptions()} />
                    </Form.Item>
                    <Form.Item label={t('discountsPage_Value')} name="discountValue" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <InputNumber min={0} className="w-100" />
                    </Form.Item>
                    <Form.Item label={t('passengerDiscountPage_MaxDiscountAmount')} name="maxDiscountAmount">
                      <InputNumber min={0} className="w-100" />
                    </Form.Item>

                    <Form.Item
                      label={t('passengerDiscountPage_ShortDescription')}
                      name="shortDescription"
                      rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                    >
                      <Input.TextArea />
                    </Form.Item>
                    <Form.Item label={t('base.Description')} name="description">
                      <Input.TextArea />
                    </Form.Item>
                    <Form.Item label={t('passengerDiscountPage_PromoPhotoUrl')} name="promoPhotoUrl">
                      <SingleImageUpload blobStorageContainer={BlobStorageContainerType.TransportPassengers} />
                    </Form.Item>
                  </Col>
                  <Col md={12}>
                    <Form.Item
                      label={t('passengerDiscountPage_ApplicationTarget')}
                      name="applicationTarget"
                      rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                    >
                      <Select mode="multiple" options={DiscountApplicationTarget.__getSelectOptions()} />
                    </Form.Item>
                    <Form.Item
                      label={t('passengerDiscountPage_DiscountUsage')}
                      name="discountUsage"
                      rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                    >
                      <Select options={DiscountUsageType.__getSelectOptions()} />
                    </Form.Item>

                    {discountUsage === DiscountUsageType.MultipleTimes ? (
                      <Form.Item
                        label={t('passengerDiscountPage_NoOfApplyTimes')}
                        name="noOfApplyTimes"
                        rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                      >
                        <InputNumber min={0} className="w-100" />
                      </Form.Item>
                    ) : null}

                    <Form.Item
                      label={t('passengerDiscountPage_Company')}
                      name="discountCompany"
                      rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                    >
                      <Select options={DiscountCompanyType.__getSelectOptions()} />
                    </Form.Item>

                    {discountCompany === DiscountCompanyType.CustomCompanies ? (
                      <Form.Item
                        label={t('passengerDiscountPage_SelectCompany')}
                        name="discountCompanyIds"
                        rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                      >
                        <CompanyInput mode="multiple" maxTagCount={undefined} ids={discount?.discountCompanyIds} />
                      </Form.Item>
                    ) : null}

                    <Form.Item
                      label={t('passengerDiscountPage_DiscountUser')}
                      name="discountUser"
                      rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                    >
                      <Select options={DiscountUserType.__getSelectOptions()} />
                    </Form.Item>

                    {discountUser === DiscountUserType.NewUsers ? (
                      <Form.Item
                        label={t('passengerDiscountPage_NewUserAfterDate')}
                        name="passengerDiscountPage_NewUserAfterDate"
                        rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                      >
                        <AppDatePicker className="w-100" />
                      </Form.Item>
                    ) : null}
                    {discountUser === DiscountUserType.OldUsers ? (
                      <Form.Item
                        label={t('passengerDiscountPage_OldUserBeforeDate')}
                        name="passengerDiscountPage_OldUserBeforeDate"
                        rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                      >
                        <AppDatePicker className="w-100" />
                      </Form.Item>
                    ) : null}

                    {discountUser === DiscountUserType.CustomUsers ? (
                      <Form.Item
                        label={t('passengerDiscountPage_SelectUsers')}
                        name="discountCustomUserIds"
                        rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                      >
                        <PassengerInput mode="multiple" maxTagCount={undefined} ids={discount?.discountCustomUserIds} />
                      </Form.Item>
                    ) : null}

                    <Form.Item label={t('base.Country')} name="countryId">
                      <CountryInput
                        allowClear
                        onChange={() => {
                          form.setFieldValue('regionId', null);
                        }}
                      />
                    </Form.Item>

                    <Form.Item label={t('base.Region')} name="regionId">
                      <RegionInput
                        onChange={() => {
                          form.setFieldValue('cityId', null);
                        }}
                        allowClear
                        countryId={countryId}
                        disabled={!countryId}
                      />
                    </Form.Item>

                    <Form.Item label={t('base.City')} name="cityId">
                      <CityInput allowClear regionId={regionId} disabled={!regionId} />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Spin>
          </Modal>
        ) : null}
      </div>
    </MainContent>
  );
};
export default PassengerDiscountPage;
